// Step 3: where tasks come from. A polled source needs a secret to list it with, its filter
// (for Jira, the query builder), a test of that filter against the real source, and how often
// to check; any source may add repositories to clone.
import { CheckSquare, GithubLogo, HandPointing, type Icon, Kanban } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import { useT } from "../../../i18n";
import { SOURCES, type Source } from "../../model";
import { type Credential, type Draft, requirementsOf, sourceConfig, sourceKinds, sourceProblem } from "../draft";
import { ChoiceCard } from "../frame/ChoiceCard";
import { JiraBuilder } from "../jira/JiraBuilder";
import { repositoriesProblem } from "../repositories";
import { CadencePicker } from "../source/CadencePicker";
import { CredentialSelect } from "../source/CredentialSelect";
import { AsanaFields, GithubFields } from "../source/PlainSourceFields";
import { PreviewPanel } from "../source/PreviewPanel";
import { RepositoriesEditor } from "../source/RepositoriesEditor";

const ICON: Record<Source, Icon> = { manual: HandPointing, github: GithubLogo, jira: Kanban, asana: CheckSquare };

function Part({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title} className="space-y-4 border-t border-muted pt-6">
      <h3 className="text-[15px] font-semibold text-gray-900">{title}</h3>
      {children}
    </section>
  );
}

interface Props {
  draft: Draft;
  onChange: (patch: Partial<Draft>) => void;
  credentials: Credential[];
  /** Add a secret of this kind, chosen for the source once saved. */
  onAdd?: (kind: string) => void;
  /** Editing a group: its source kind cannot change, so only that card shows. */
  fixedSource?: boolean;
}

export function SourceStep({ draft, onChange, credentials, onAdd, fixedSource = false }: Props) {
  const { tasks } = useT();
  const t = tasks.create.source;
  const kinds = sourceKinds(draft.source);
  const matches = credentials.filter((c) => kinds.includes(c.kind));
  const pick = (source: Source) => {
    if (source === draft.source) return;
    const usable = credentials.filter((c) => sourceKinds(source).includes(c.kind));
    onChange({ source, credentialId: usable.length === 1 ? usable[0]!.id : "", bindings: {} });
  };
  const problem = sourceProblem(draft);
  const filterReady = problem === null || problem === "cadence" || problem === "repositories";
  const request = draft.source !== "manual" && filterReady ? { source: draft.source, source_config: sourceConfig(draft), source_credential_id: draft.credentialId } : null;
  const needsGithub = requirementsOf(draft.workflow).some((r) => r.kind === "credential" && (r.credential_kind === "github_pat" || r.credential_kind === "github_app"));
  return (
    <div className="space-y-8">
      <div role="radiogroup" aria-label={t.label} className="grid gap-3 sm:grid-cols-2">
        {SOURCES.filter((s) => !fixedSource || s === draft.source).map((s) => {
          const Glyph = ICON[s];
          return (
            <ChoiceCard
              key={s}
              name="source"
              value={s}
              checked={draft.source === s}
              onSelect={() => pick(s)}
              icon={<Glyph weight="duotone" className="mt-0.5 h-6 w-6 shrink-0 text-primary" aria-hidden />}
              title={tasks.source[s]}
            >
              {t.hints[s]}
            </ChoiceCard>
          );
        })}
      </div>
      {draft.source === "manual" ? (
        <p className="m-0 rounded-lg bg-gray-100 p-3 text-[13px] text-gray-600">{t.manualNote}</p>
      ) : (
        <>
          <Part title={t.connect}>
            <CredentialSelect
              label={t.connectWith(tasks.source[draft.source])}
              matches={matches}
              value={draft.credentialId}
              onChange={(credentialId) => onChange({ credentialId, bindings: {} })}
              onAdd={onAdd && (() => onAdd(kinds[0]!))}
              missing={t.noSecret(tasks.source[draft.source])}
            />
          </Part>
          <Part title={draft.source === "jira" ? t.whichIssues : t.filter}>
            {draft.source === "jira" ? <JiraBuilder jira={draft.jira} credentialId={draft.credentialId} onChange={(jira) => onChange({ jira })} /> : null}
            {draft.source === "github" ? <GithubFields draft={draft} onChange={onChange} /> : null}
            {draft.source === "asana" ? <AsanaFields draft={draft} onChange={onChange} /> : null}
            <PreviewPanel request={request} unavailable={draft.credentialId === "" ? t.testNeedsSecret : filterReady ? null : t.testNeedsFilter} />
          </Part>
          <CadencePicker value={draft.cadence} onChange={(cadence) => onChange({ cadence })} />
        </>
      )}
      <RepositoriesEditor
        rows={draft.repositories}
        onChange={(repositories) => onChange({ repositories })}
        problem={repositoriesProblem(draft.repositories, draft.source === "github" ? draft.github.repo.trim() : "")}
        advise={needsGithub && draft.source !== "github"}
      />
    </div>
  );
}
